import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';

// Types
interface DietaryRestriction {
  name: string;
  type: 'allergy' | 'intolerance' | 'preference' | 'lifestyle';
}

interface CookingHabit {
  frequency: string;
  preferredMealTypes: string[];
  cookingSkillLevel: string;
  timeAvailable: string;
}

interface FoodPreference {
  cuisines: string[];
  flavors: string[];
  mealTypes: string[];
  snackTypes: string[];
}

interface TasteTestData {
  foodPreferences: FoodPreference;
  dietaryRestrictions: DietaryRestriction[];
  cookingHabits: CookingHabit;
  takeawayNight: string;
  budgetRange: string;
  shoppingPreferences: string[];
}

interface TasteTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: TasteTestData) => void;
  initialData?: Partial<TasteTestData>;
}

// Styled Components
const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 80px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
  padding: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 70px;
    padding: ${theme.spacing.sm};
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  max-width: 700px;
  width: 90%;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${theme.colors.primaryLight}20;
  position: relative;
  
  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin: 16px;
    width: calc(100% - 32px);
    max-height: calc(100vh - 32px);
    border-radius: 20px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h2`
  color: ${theme.colors.deepDark};
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  svg {
    color: ${theme.colors.secondary};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${theme.colors.text};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    color: ${theme.colors.deepDark};
  }
`;

const StepContainer = styled.div`
  padding: ${theme.spacing.lg};
  min-height: 350px;
  display: flex;
  flex-direction: column;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    min-height: 300px;
  }
`;

const StepTitle = styled.h3`
  color: ${theme.colors.deepDark};
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: ${theme.spacing.sm};
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.1rem;
    margin-bottom: ${theme.spacing.xs};
  }
`;

const StepDescription = styled.p`
  color: ${theme.colors.text};
  font-size: 0.95rem;
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
  line-height: 1.5;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.9rem;
    margin-bottom: ${theme.spacing.md};
    line-height: 1.4;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: ${theme.spacing.xs};
  }
`;

const OptionTag = styled.button<{ isSelected: boolean }>`
  background-color: ${props => props.isSelected ? theme.colors.secondary : 'white'};
  color: ${props => props.isSelected ? 'white' : theme.colors.text};
  border: 1px solid ${props => props.isSelected ? theme.colors.secondary : 'rgba(0, 0, 0, 0.2)'};
  border-radius: 20px;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  min-height: ${theme.mobile.minTouchTarget};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.isSelected ? theme.colors.secondary : theme.colors.secondary + '30'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: 0.8rem;
    min-height: 40px;
    
    &:hover {
      transform: none;
    }
    
    &:active {
      transform: scale(0.95);
    }
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.md};
  }
`;

const Label = styled.label`
  display: block;
  color: ${theme.colors.deepDark};
  font-weight: 500;
  margin-bottom: ${theme.spacing.sm};
  font-size: 0.95rem;
`;

const Select = styled.select`
  width: 100%;
  padding: ${theme.spacing.sm};
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  font-size: 0.9rem;
  background-color: white;
  color: ${theme.colors.text};
  min-height: ${theme.mobile.minTouchTarget};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary};
    box-shadow: 0 0 0 3px ${theme.colors.secondary}30;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 44px;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.sm};
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  font-size: 0.9rem;
  background-color: white;
  color: ${theme.colors.text};
  min-height: ${theme.mobile.minTouchTarget};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.secondary};
    box-shadow: 0 0 0 3px ${theme.colors.secondary}30;
  }
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.5);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    font-size: 16px; /* Prevents zoom on iOS */
    min-height: 44px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing.lg};
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.md};
    gap: ${theme.spacing.sm};
  }
`;

const NavButton = styled.button<{ variant?: 'primary'; disabled?: boolean }>`
  background-color: ${props => 
    props.variant === 'primary' ? theme.colors.secondary : 'transparent'
  };
  color: ${props => 
    props.variant === 'primary' ? 'white' : theme.colors.text
  };
  border: 1px solid ${props => 
    props.variant === 'primary' ? theme.colors.secondary : 'rgba(0, 0, 0, 0.2)'
  };
  border-radius: 12px;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  min-height: ${theme.mobile.minTouchTarget};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: 0.85rem;
    min-height: 44px;
    
    &:hover:not(:disabled) {
      transform: none;
    }
    
    &:active:not(:disabled) {
      transform: scale(0.95);
    }
  }
`;

const StepIndicator = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
`;

const StepDot = styled.div<{ isActive: boolean; isCompleted: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${props => 
    props.isActive ? theme.colors.secondary :
    props.isCompleted ? theme.colors.primary :
    'rgba(0, 0, 0, 0.2)'
  };
  transition: all 0.2s ease;
`;

const TasteTestModal: React.FC<TasteTestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = {}
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [foodPreferences, setFoodPreferences] = useState<FoodPreference>({
    cuisines: [],
    flavors: [],
    mealTypes: [],
    snackTypes: []
  });
  const [dietaryRestrictions, setDietaryRestrictions] = useState<DietaryRestriction[]>([]);
  const [cookingHabits, setCookingHabits] = useState<CookingHabit>({
    frequency: '',
    preferredMealTypes: [],
    cookingSkillLevel: '',
    timeAvailable: ''
  });
  const [takeawayNight, setTakeawayNight] = useState<string>('');
  const [hasTakeawayNight, setHasTakeawayNight] = useState<boolean | null>(null);
  const [takeawayNights, setTakeawayNights] = useState<string[]>([]);
  const [budgetRange, setBudgetRange] = useState<string>('');
  const [shoppingPreferences, setShoppingPreferences] = useState<string[]>([]);

  // Options data
  const cuisineOptions = [
    'Italian', 'Chinese', 'Mexican', 'Indian', 'Thai', 'Japanese', 'Mediterranean',
    'French', 'American', 'Korean', 'Vietnamese', 'Greek', 'Spanish', 'Middle Eastern'
  ];

  const flavorOptions = [
    'Spicy', 'Sweet', 'Salty', 'Sour', 'Bitter', 'Umami', 'Mild', 'Bold',
    'Smoky', 'Fresh', 'Creamy', 'Crunchy', 'Tangy', 'Rich'
  ];

  const mealTypeOptions = [
    'Quick & Easy', 'Comfort Food', 'Healthy', 'Gourmet', 'One-Pot Meals',
    'Meal Prep Friendly', 'Family Style', 'Light Meals', 'Hearty Meals'
  ];

  const snackOptions = [
    'Sweet Treats', 'Savory Snacks', 'Healthy Options', 'Quick Bites',
    'Protein-Rich', 'Fresh Fruits', 'Nuts & Seeds', 'Crackers & Cheese'
  ];

  const allergyOptions = [
    { name: 'Nuts', type: 'allergy' as const },
    { name: 'Dairy', type: 'allergy' as const },
    { name: 'Gluten', type: 'allergy' as const },
    { name: 'Shellfish', type: 'allergy' as const },
    { name: 'Eggs', type: 'allergy' as const },
    { name: 'Soy', type: 'allergy' as const },
    { name: 'Fish', type: 'allergy' as const },
    { name: 'Lactose', type: 'intolerance' as const },
    { name: 'Vegetarian', type: 'lifestyle' as const },
    { name: 'Vegan', type: 'lifestyle' as const },
    { name: 'Keto', type: 'lifestyle' as const },
    { name: 'Paleo', type: 'lifestyle' as const }
  ];

  const shoppingOptions = [
    'Weekly Grocery Shopping', 'Daily Fresh Shopping', 'Online Delivery',
    'Bulk Buying', 'Local Markets', 'Organic Focus', 'Budget Conscious',
    'Meal Kit Services'
  ];

  const dayOptions = [
    'Monday',
    'Tuesday', 
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  useEffect(() => {
    if (isOpen && initialData) {
      if (initialData.foodPreferences) setFoodPreferences(initialData.foodPreferences);
      if (initialData.dietaryRestrictions) setDietaryRestrictions(initialData.dietaryRestrictions);
      if (initialData.cookingHabits) setCookingHabits(initialData.cookingHabits);
      if (initialData.takeawayNight) {
        setTakeawayNight(initialData.takeawayNight);
        // If there's existing takeaway night data, set up the new states
        if (initialData.takeawayNight.includes(',')) {
          // Multiple nights stored as comma-separated string
          setTakeawayNights(initialData.takeawayNight.split(','));
          setHasTakeawayNight(true);
        } else if (initialData.takeawayNight) {
          // Single night
          setTakeawayNights([initialData.takeawayNight]);
          setHasTakeawayNight(true);
        }
      }
      if (initialData.budgetRange) setBudgetRange(initialData.budgetRange);
      if (initialData.shoppingPreferences) setShoppingPreferences(initialData.shoppingPreferences);
    }
  }, [isOpen, initialData]);

  const toggleSelection = (array: string[], item: string, setter: (arr: string[]) => void) => {
    if (array.includes(item)) {
      setter(array.filter(i => i !== item));
    } else {
      setter([...array, item]);
    }
  };

  const toggleRestriction = (restriction: DietaryRestriction) => {
    const exists = dietaryRestrictions.find(r => r.name === restriction.name);
    if (exists) {
      setDietaryRestrictions(dietaryRestrictions.filter(r => r.name !== restriction.name));
    } else {
      setDietaryRestrictions([...dietaryRestrictions, restriction]);
    }
  };

  const toggleTakeawayNight = (day: string) => {
    if (takeawayNights.includes(day)) {
      setTakeawayNights(takeawayNights.filter(night => night !== day));
    } else {
      setTakeawayNights([...takeawayNights, day]);
    }
  };

  const handleTakeawayNightChange = (hasNight: boolean) => {
    setHasTakeawayNight(hasNight);
    if (!hasNight) {
      setTakeawayNights([]);
      setTakeawayNight('');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return foodPreferences.cuisines.length > 0 || foodPreferences.flavors.length > 0;
      case 1:
        return true; // Dietary restrictions are optional
      case 2:
        return cookingHabits.frequency !== '';
      case 3:
        return hasTakeawayNight !== null && 
               (hasTakeawayNight === false || takeawayNights.length > 0);
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = () => {
    // Convert takeaway nights array to string for backward compatibility
    const takeawayNightString = hasTakeawayNight ? takeawayNights.join(',') : '';
    
    onSubmit({
      foodPreferences,
      dietaryRestrictions,
      cookingHabits,
      takeawayNight: takeawayNightString,
      budgetRange,
      shoppingPreferences
    });
    onClose();
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepContainer>
            <StepTitle>What flavors make your taste buds dance?</StepTitle>
            <StepDescription>
              Tell us about your favorite cuisines and flavors. This helps us suggest meals you'll actually want to eat!
            </StepDescription>
            
            <FormGroup>
              <Label>Favorite Cuisines</Label>
              <OptionsGrid>
                {cuisineOptions.map(cuisine => (
                  <OptionTag
                    key={cuisine}
                    isSelected={foodPreferences.cuisines.includes(cuisine)}
                    onClick={() => toggleSelection(
                      foodPreferences.cuisines, 
                      cuisine, 
                      (cuisines) => setFoodPreferences({...foodPreferences, cuisines})
                    )}
                  >
                    {cuisine}
                  </OptionTag>
                ))}
              </OptionsGrid>
            </FormGroup>

            <FormGroup>
              <Label>Flavor Preferences</Label>
              <OptionsGrid>
                {flavorOptions.map(flavor => (
                  <OptionTag
                    key={flavor}
                    isSelected={foodPreferences.flavors.includes(flavor)}
                    onClick={() => toggleSelection(
                      foodPreferences.flavors, 
                      flavor, 
                      (flavors) => setFoodPreferences({...foodPreferences, flavors})
                    )}
                  >
                    {flavor}
                  </OptionTag>
                ))}
              </OptionsGrid>
            </FormGroup>
          </StepContainer>
        );

      case 1:
        return (
          <StepContainer>
            <StepTitle>Any foods we should avoid?</StepTitle>
            <StepDescription>
              Let us know about allergies, intolerances, or dietary choices so we can keep you safe and happy.
            </StepDescription>
            
            <OptionsGrid>
              {allergyOptions.map(option => (
                <OptionTag
                  key={option.name}
                  isSelected={dietaryRestrictions.some(r => r.name === option.name)}
                  onClick={() => toggleRestriction(option)}
                >
                  {option.name}
                </OptionTag>
              ))}
            </OptionsGrid>
          </StepContainer>
        );

      case 2:
        return (
          <StepContainer>
            <StepTitle>How do you like to cook?</StepTitle>
            <StepDescription>
              Tell us about your cooking style so we can suggest recipes that fit your lifestyle.
            </StepDescription>
            
            <FormGroup>
              <Label>How often do you cook?</Label>
              <Select 
                value={cookingHabits.frequency} 
                onChange={(e) => setCookingHabits({...cookingHabits, frequency: e.target.value})}
              >
                <option value="">Select frequency</option>
                <option value="daily">Daily</option>
                <option value="few-times-week">A few times a week</option>
                <option value="weekly">Weekly</option>
                <option value="occasionally">Occasionally</option>
                <option value="rarely">Rarely</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Cooking skill level</Label>
              <Select 
                value={cookingHabits.cookingSkillLevel} 
                onChange={(e) => setCookingHabits({...cookingHabits, cookingSkillLevel: e.target.value})}
              >
                <option value="">Select skill level</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="expert">Expert</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>How much time do you usually have for cooking?</Label>
              <Select 
                value={cookingHabits.timeAvailable} 
                onChange={(e) => setCookingHabits({...cookingHabits, timeAvailable: e.target.value})}
              >
                <option value="">Select time</option>
                <option value="15-minutes">15 minutes or less</option>
                <option value="30-minutes">30 minutes</option>
                <option value="1-hour">1 hour</option>
                <option value="2-hours">2+ hours</option>
                <option value="varies">It varies</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Preferred meal types</Label>
              <OptionsGrid>
                {mealTypeOptions.map(type => (
                  <OptionTag
                    key={type}
                    isSelected={cookingHabits.preferredMealTypes.includes(type)}
                    onClick={() => toggleSelection(
                      cookingHabits.preferredMealTypes, 
                      type, 
                      (types) => setCookingHabits({...cookingHabits, preferredMealTypes: types})
                    )}
                  >
                    {type}
                  </OptionTag>
                ))}
              </OptionsGrid>
            </FormGroup>
          </StepContainer>
        );

      case 3:
        return (
          <StepContainer>
            <StepTitle>Let's talk about the practical stuff</StepTitle>
            <StepDescription>
              A few more details to help us create the perfect meal plans for your household.
            </StepDescription>
            
            <FormGroup>
              <Label>Do you have a takeaway night?</Label>
              <OptionsGrid>
                <OptionTag
                  isSelected={hasTakeawayNight === true}
                  onClick={() => handleTakeawayNightChange(true)}
                >
                  Yes
                </OptionTag>
                <OptionTag
                  isSelected={hasTakeawayNight === false}
                  onClick={() => handleTakeawayNightChange(false)}
                >
                  No
                </OptionTag>
              </OptionsGrid>
            </FormGroup>

            {hasTakeawayNight === true && (
              <FormGroup>
                <Label>Which day(s)?</Label>
                <OptionsGrid>
                  {dayOptions.map(day => (
                    <OptionTag
                      key={day}
                      isSelected={takeawayNights.includes(day)}
                      onClick={() => toggleTakeawayNight(day)}
                    >
                      {day}
                    </OptionTag>
                  ))}
                </OptionsGrid>
              </FormGroup>
            )}

            <FormGroup>
              <Label>Weekly food budget ($)</Label>
              <Input 
                type="number"
                placeholder="Enter your weekly food budget (optional)"
                value={budgetRange} 
                onChange={(e) => setBudgetRange(e.target.value)}
                min="0"
                step="10"
              />
            </FormGroup>

            <FormGroup>
              <Label>Shopping preferences</Label>
              <OptionsGrid>
                {shoppingOptions.map(option => (
                  <OptionTag
                    key={option}
                    isSelected={shoppingPreferences.includes(option)}
                    onClick={() => toggleSelection(shoppingPreferences, option, setShoppingPreferences)}
                  >
                    {option}
                  </OptionTag>
                ))}
              </OptionsGrid>
            </FormGroup>
          </StepContainer>
        );

      default:
        return null;
    }
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.20-1.10-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
            </svg>
            The Taste Test
          </ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>

        {renderStep()}

        <ButtonContainer>
          <NavButton 
            onClick={handlePrevious} 
            disabled={currentStep === 0}
          >
            Previous
          </NavButton>
          
          <StepIndicator>
            {[0, 1, 2, 3].map(step => (
              <StepDot
                key={step}
                isActive={step === currentStep}
                isCompleted={step < currentStep}
              />
            ))}
          </StepIndicator>
          
          {currentStep < 3 ? (
            <NavButton 
              variant="primary" 
              onClick={handleNext}
              disabled={!canProceed()}
            >
              Next
            </NavButton>
          ) : (
            <NavButton 
              variant="primary" 
              onClick={handleSubmit}
            >
              Complete Test
            </NavButton>
          )}
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default TasteTestModal; 